import { installAccountingHub } from "@intrasync/accounting";
import RootStore from "./stores/RootStore";
import rootStoreConfig from "./rootStoreConfig";

interface Window {
  rootStore?: RootStore;
  dateFNS?: unknown;
  __STORE_COUNT__?: number;
  __MEMORY_MONITOR__?: unknown;
}

// Host-contract wiring for @intrasync/accounting. The AccountingHub reads the
// active tenant identifier from rootStore.companyId (via getCompanyId()); the
// trial-balance and every company-scoped service reject an empty companyId.
// Premier is single-tenant, so this is the Premier Precast company identifier.
// The wire column is `companyId NVARCHAR(50)` — a free string (not an int, not a
// GUID), so a readable slug is fine; it only has to be NON-EMPTY, <=50 chars, and
// CONSISTENT (it scopes all accounting data via unique [companyId, *] indexes).
// TODO(equipo): confirmar el companyId canónico de Premier con Zach y, idealmente,
// tomarlo de configuración o del usuario autenticado en lugar de esta constante.
// PLACEHOLDER hasta esa confirmación.
const PREMIER_COMPANY_ID = "premier-precast";

export function createRootStore(): RootStore {
  const rootStore = new RootStore(rootStoreConfig);

  (rootStore as RootStore & { companyId?: string }).companyId =
    PREMIER_COMPANY_ID;

  // Wire the @intrasync/accounting hub. This single call eager-mounts the
  // accounting entity ServiceStores synchronously (own-properties of the
  // rootStore BEFORE first render, so injectStores/mobx-react finds them in the
  // frozen Provider snapshot — do NOT also spread them into
  // serviceStoresConfig.ts), registers the socket custom methods
  // (invoices.finalize, bills.approve, payments.createAndApply, …), and installs
  // the host-contract permissions resolver. companyId stays the host-stamped
  // rootStore.companyId above (the installer's default read), so no
  // resolveCompanyId is needed.
  //
  // FEATURES are deliberately NOT passed here: the runtime truth is the
  // per-company DB row managed in the hub (Settings → Module Features); with no
  // row, the module's tri-state defaults rule (poApproval/SOV/job-costing stay
  // hidden until enabled from the panel; reconciliation defaults ON — turn it
  // Off from the panel to keep Premier's previous opt-out). Do not add flags here.
  //
  // PERMISSIONS — Premier has no fine-grained accounting role model yet, so we
  // mirror Premier's OWN admin convention (RouterStore PermissionManager:
  // `currentUser?.adminUser`): a Premier admin gets every accounting capability;
  // everyone else gets none of the gated ones. Read LIVE (the resolver runs on
  // every access) so it reflects the user post-login.
  // TODO(equipo): para control fino (p. ej. void != approve, o admin de
  // accounting != admin general), mapear el sistema de user-permissions/
  // role-permissions de Premier a las AccountingCapabilities en lugar de este
  // flag binario.
  installAccountingHub(
    rootStore as unknown as Parameters<typeof installAccountingHub>[0],
    rootStore.app,
    {
      resolvePermissions: () => ({
        has: () =>
          Boolean(
            (
              rootStore.authStore?.currentUser as
                | { adminUser?: boolean }
                | undefined
            )?.adminUser,
          ),
      }),
    },
  );

  // if (import.meta.env.MODE === "development") {
  (window as Window).rootStore = rootStore;
  // }

  return rootStore;
}
